import { useModalApiContext } from '@vivli/core/infrastructure/context';
import { uploadActivityService } from '@vivli/shared/infrastructure/service';

// Exact wording from DEV-485. Exported so both the router guard (UploadNavigationGuard) and the
// local-state tab guards use identical copy.
export const UPLOAD_IN_PROGRESS_MESSAGE =
    'An upload of one or more files is underway. If you leave this page, that will cancel the upload of any file(s) that have not yet completed.';

/**
 * Returns a function that, when a file upload is in progress, prompts the user before they leave and
 * resolves the choice: `true` = leave (any not-yet-complete uploads are cancelled first), `false` =
 * stay. When nothing is uploading it resolves `true` immediately (no prompt).
 *
 * Shared by every "leaving" path so behavior/copy stay identical: the React Router `useBlocker` guard
 * (UploadNavigationGuard) and local-state tab switches that `useBlocker` can't see (e.g. the dashboard
 * tabs). (DEV-485)
 */
export const useConfirmLeaveDuringUpload = () => {
    const modalService = useModalApiContext();

    return (): Promise<boolean> =>
        new Promise((resolve) => {
            if (!uploadActivityService.hasActiveUploads()) {
                resolve(true);
                return;
            }

            modalService.confirm(UPLOAD_IN_PROGRESS_MESSAGE, {
                title: 'Upload in progress',
                // "Continue Upload" = stay and keep uploading.
                confirmText: 'Continue Upload',
                // "Cancel Upload" = abort the not-yet-complete uploads, then leave.
                cancelText: 'Cancel Upload',
                onConfirm: () => resolve(false),
                onCancel: () => {
                    uploadActivityService.cancelAllActiveUploads();
                    resolve(true);
                }
            });
        });
};
